// Explanation text / speech / audio helpers extracted verbatim from
// InteractiveGraph.js (IG-split Slice 4, pure-function seam). Pure string and
// data transforms; the emoji helpers they lean on are imported from the
// already-extracted ./emoji module. addEmojiToExplanationLine stays
// module-internal (only enrichExplanationText consumes it), matching its
// original inline scope.
import { stripEmojis, startsWithEmoji, getSmartEmojiForLabel } from './emoji';

export const MAX_EXPLANATION_AUDIO_CACHE_ENTRIES = 8;
export const TTS_SLOW_WARNING_DELAY_MS = 8000;

export const renderMarkdownText = (text = '') => {
  if (!text) return [];
  const result = [];
  const regex = /\*\*(.+?)\*\*/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      result.push({ type: 'text', content: text.slice(lastIndex, match.index) });
    }
    result.push({ type: 'bold', content: match[1] });
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    result.push({ type: 'text', content: text.slice(lastIndex) });
  }

  return result.length ? result : [{ type: 'text', content: text }];
};

export const cleanTextForSpeech = (rawText = '') => {
  if (!rawText) return '';
  let text = rawText;
  text = stripEmojis(text);
  text = text.replace(/\*\*/g, '');
  text = text.replace(/\*(?!\*)/g, '');
  text = text.replace(/^#+\s+/gm, '');
  text = text.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
  text = text.replace(/```[a-z]*\n/gi, '');
  text = text.replace(/```/g, '');
  text = text.replace(/`([^`]+)`/g, '$1');
  text = text.replace(/\s+/g, ' ');
  return text.trim();
};

const addEmojiToExplanationLine = (line) => {
  if (!line) return '';
  const trimmed = line.trim();
  if (!trimmed.length) return '';

  if (startsWithEmoji(trimmed)) {
    return trimmed;
  }

  const emojiCandidate = getSmartEmojiForLabel(trimmed);
  const emoji = emojiCandidate && emojiCandidate !== '🔹' ? emojiCandidate : null;
  if (!emoji) {
    return trimmed;
  }

  const bulletMatch = trimmed.match(/^([-*•・]\s+)(.+)$/);
  if (bulletMatch) {
    return `${bulletMatch[1]}${emoji} ${bulletMatch[2]}`;
  }

  const orderedMatch = trimmed.match(/^(\d+[.)]\s+)(.+)$/);
  if (orderedMatch) {
    return `${orderedMatch[1]}${emoji} ${orderedMatch[2]}`;
  }

  return `${emoji} ${trimmed}`;
};

export const enrichExplanationText = (text = '') => {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => addEmojiToExplanationLine(line));
};

export const pickFirstNonEmptyText = (candidates = []) => {
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      return candidate;
    }
    if (Array.isArray(candidate)) {
      const joined = candidate
        .filter((segment) => segment !== null && segment !== undefined)
        .map((segment) => segment.toString())
        .join('\n')
        .trim();
      if (joined.length > 0) {
        return joined;
      }
    }
    if (candidate && typeof candidate === 'object') {
      const nested = candidate.text || candidate.value || candidate.content;
      if (typeof nested === 'string' && nested.trim().length > 0) {
        return nested;
      }
    }
  }
  return '';
};

export const pickFirstValidUrl = (candidates = []) => {
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      return candidate.trim();
    }
  }
  return '';
};

export const createAudioUrlFromBase64 = (base64, mimeType = 'audio/mp3') => {
  if (!base64) return '';
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i += 1) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: mimeType });
  return URL.createObjectURL(blob);
};
